/**
 * Level metering driven by the worklet's per-block level messages —
 * no rAF loop closing over stale React state (the old frozen-meter bug).
 */

export interface MeterState {
  rmsDb: number;       // smoothed
  peakDb: number;      // instantaneous block peak
  peakHoldDb: number;  // decaying peak-hold
  clipped: boolean;    // clip flash, true for ~300ms after a clipped block
  tooQuiet: boolean;   // rms below -35 dBFS for >2s of speech-ish activity
}

const SILENCE_FLOOR_DB = -65;
// Raw (pre-makeup-gain) worklet levels: with browser AGC off, honest mic
// input at reading distance is -35..-47 dB RMS and encode-time gain fixes
// the file level. Only nag when the raw signal risks the +30 dB gain cap.
const TOO_QUIET_DB = -45;
const TOO_QUIET_HOLD_MS = 2000;
const CLIP_FLASH_MS = 300;
const PEAK_HOLD_DECAY_DB_PER_S = 12;

export function toDb(linear: number): number {
  return linear <= 1e-7 ? -140 : 20 * Math.log10(linear);
}

export class LevelMeter {
  private state: MeterState = {
    rmsDb: -140, peakDb: -140, peakHoldDb: -140, clipped: false, tooQuiet: false,
  };
  private smoothedRms = 0;
  private lastClipAt = 0;
  private quietSince: number | null = null;
  private lastUpdateAt = 0;

  /** Feed one 'level' message from the worklet. Returns the new state. */
  update(rms: number, peak: number, clippedSamples: number): MeterState {
    const now = performance.now();
    const dt = this.lastUpdateAt ? (now - this.lastUpdateAt) / 1000 : 0;
    this.lastUpdateAt = now;

    // Fast attack, slow release — reads naturally on a bar.
    const alpha = rms > this.smoothedRms ? 0.5 : 0.15;
    this.smoothedRms += alpha * (rms - this.smoothedRms);

    const rmsDb = toDb(this.smoothedRms);
    const peakDb = toDb(peak);

    if (clippedSamples > 0) this.lastClipAt = now;

    let peakHoldDb = this.state.peakHoldDb - PEAK_HOLD_DECAY_DB_PER_S * dt;
    if (peakDb > peakHoldDb) peakHoldDb = peakDb;

    // "Too quiet" only counts while there is *some* signal (not dead silence),
    // so it reads as "speaking but too far from the mic".
    if (rmsDb > SILENCE_FLOOR_DB && rmsDb < TOO_QUIET_DB) {
      if (this.quietSince === null) this.quietSince = now;
    } else {
      this.quietSince = null;
    }

    this.state = {
      rmsDb,
      peakDb,
      peakHoldDb,
      clipped: now - this.lastClipAt < CLIP_FLASH_MS,
      tooQuiet: this.quietSince !== null && now - this.quietSince > TOO_QUIET_HOLD_MS,
    };
    return this.state;
  }

  reset(): void {
    this.smoothedRms = 0;
    this.quietSince = null;
    this.lastClipAt = 0;
    this.lastUpdateAt = 0;
    this.state = {
      rmsDb: -140, peakDb: -140, peakHoldDb: -140, clipped: false, tooQuiet: false,
    };
  }
}
